import { PushNotifications } from '@capacitor/push-notifications';
import { useHttpMutation } from '../../api';
import { API_URLS } from '../consts';
import { useCallback, useEffect, useRef } from 'react';
import { LoggerService } from '../logger';
import { Capacitor } from '@capacitor/core';
import { dispatch, getStoreState, Store } from '../../redux';
import { useSelector } from 'react-redux';
import { BhabiStorage } from './storage';
import { useNavigate } from 'react-router-dom';
import { getUser, updateUserState } from '@/redux/reducers';
import { noop } from 'lodash';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { firebaseApp } from './firebase';
import { ENV } from '../env';

export const usePushNotifications = (showNotificationDeniedPopup = noop) => {
  const { mutate: updateToken } = useHttpMutation<
    { message: string },
    { token: string; device: string }
  >(API_URLS.IOS_NOTIFICATION_TOKEN, 'POST');

  const { mutate: updateLastTimeAskedForIOSToken } = useHttpMutation<
    { message: string },
    void
  >(API_URLS.UPDATE_IOS_TOKEN_PROMPTED, 'PATCH');

  const { isInRoom } = useSelector((state: Store) => state.userReducer);
  const { mutate: reJoinRoom } = useHttpMutation<
    { token: string },
    { roomID: string }
  >(API_URLS.RE_JOIN_ROOM, 'POST');
  const nav = useNavigate();

  const pushNotificationRef = useRef<string>('');
  const permissionCheckedRef = useRef<boolean>(false);
  const webPermissionCheckedRef = useRef<boolean>(false);

  const addListeners = useCallback(async () => {
    await PushNotifications.addListener('registration', token => {
      LoggerService.info('Push notification registering');
      if (pushNotificationRef.current === token.value) {
        return;
      }
      pushNotificationRef.current = token.value;

      updateToken(
        { token: token.value, device: Capacitor.getPlatform() },
        {
          onSuccess: () => {
            LoggerService.info('Push notification registered');
            dispatch(updateUserState({ registeredNotificationToken: true }));
          },
          onError: () => {
            LoggerService.error('Push notification registration failed');
          },
        }
      );
    });

    await PushNotifications.addListener('registrationError', err => {
      LoggerService.error('Registration error: ', err.error);
    });

    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      async ({ notification }) => {
        LoggerService.info('Push notification received', !!notification.data);
        const { roomID } = notification.data || {};
        if (isInRoom || !roomID || Capacitor.getPlatform() !== 'ios') {
          return;
        }

        reJoinRoom(
          { roomID },
          {
            onSuccess: res => {
              BhabiStorage.setToken(res.token);
              dispatch(getUser(res.token));
              nav('/in-play');
            },
            onError: () => {
              LoggerService.error('Re-join room failed');
            },
          }
        );
      }
    );
  }, [isInRoom, nav, reJoinRoom, updateToken]);

  const updateLastTimeAsked = useCallback(() => {
    updateLastTimeAskedForIOSToken(undefined, {
      onSuccess: () => {
        dispatch(
          updateUserState({
            lastTimeAskedForNotificationToken: new Date().toISOString(),
          })
        );
      },
    });
  }, [updateLastTimeAskedForIOSToken]);

  const remindUserAfterSevenDaysIfTheyDenied = useCallback(() => {
    const lastAskedTime =
      getStoreState().userReducer.lastTimeAskedForNotificationToken;
    const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    const now = new Date().getTime();

    LoggerService.warn('Push notification permission denied', {
      lastAskedTime,
      now,
    });

    if (lastAskedTime) {
      const timeSinceLastAsked = now - new Date(lastAskedTime).getTime();
      if (timeSinceLastAsked < sevenDaysInMs) {
        LoggerService.error('No notification permissions granted');
        return;
      }
    }

    LoggerService.info('Suggesting user to enable notifications');
    showNotificationDeniedPopup();
    updateLastTimeAsked();
  }, [showNotificationDeniedPopup, updateLastTimeAsked]);

  // Web push notification handler
  const registerWebPushNotifications = useCallback(async () => {
    try {
      // Skip if already checked or if service workers not supported
      if (webPermissionCheckedRef.current || !('serviceWorker' in navigator)) {
        return;
      }
      webPermissionCheckedRef.current = true;

      LoggerService.info('Registering web push notifications');

      // Register service worker and wait for it to be active
      const registration = await navigator.serviceWorker.register(
        '/firebase-messaging-sw.js',
        { scope: '/' }
      );
      LoggerService.info('Service Worker registration started');

      // Wait for the service worker to be active
      let activeWorker = registration.active;
      if (!activeWorker) {
        // If not active yet, wait for it
        await new Promise<void>(resolve => {
          if (registration.active) {
            resolve();
          } else {
            const onStateChange = () => {
              if (
                registration.active ||
                (registration.installing &&
                  registration.installing.state === 'activated') ||
                (registration.waiting &&
                  registration.waiting.state === 'activated')
              ) {
                if (registration.installing) {
                  registration.installing.removeEventListener(
                    'statechange',
                    onStateChange
                  );
                }
                if (registration.waiting) {
                  registration.waiting.removeEventListener(
                    'statechange',
                    onStateChange
                  );
                }
                resolve();
              }
            };
            if (registration.installing) {
              registration.installing.addEventListener(
                'statechange',
                onStateChange
              );
            }
            if (registration.waiting) {
              registration.waiting.addEventListener(
                'statechange',
                onStateChange
              );
            }
          }
        });
      }
      LoggerService.info('Service Worker is now active');

      // Get Firebase messaging instance
      const messaging = getMessaging(firebaseApp);

      // Helper function to get and register FCM token
      const getAndRegisterFCMToken = async () => {
        try {
          LoggerService.info('Attempting to get FCM token');
          const fcmToken = await getToken(messaging, {
            vapidKey: ENV.REACT_APP_FCM_VAPID_KEY,
          });

          if (fcmToken) {
            LoggerService.info('FCM Token received for web');
            updateToken(
              // For android and web there is same way from backend to send push notifications
              { token: fcmToken, device: 'android' },
              {
                onSuccess: () => {
                  LoggerService.info('Web push notification token registered');
                  dispatch(
                    updateUserState({ registeredNotificationToken: true })
                  );
                },
                onError: () => {
                  LoggerService.error(
                    'Web push notification registration failed'
                  );
                },
              }
            );
          } else {
            LoggerService.warn('No FCM token received');
          }
        } catch (err) {
          LoggerService.error('Error getting FCM token:', err);
        }
      };

      // Request notification permission if not already granted
      if (Notification.permission === 'granted') {
        LoggerService.info('Notification permission already granted');
        await getAndRegisterFCMToken();
      } else if (Notification.permission === 'default') {
        LoggerService.info('Requesting notification permission');
        const permission = await Notification.requestPermission();
        updateLastTimeAsked();
        if (permission === 'granted') {
          LoggerService.info('Notification permission granted by user');
          await getAndRegisterFCMToken();
        } else {
          remindUserAfterSevenDaysIfTheyDenied();
          LoggerService.warn('Notification permission denied by user');
        }
      } else {
        LoggerService.warn('Notification permission is denied');
      }

      // Listen to foreground messages (when app is open)
      onMessage(messaging, payload => {
        LoggerService.info('Message received in foreground:', payload);
        const { roomID } = payload.data || {};

        // Handle foreground message
        if (!isInRoom && roomID) {
          reJoinRoom(
            { roomID },
            {
              onSuccess: res => {
                BhabiStorage.setToken(res.token);
                dispatch(getUser(res.token));
                nav('/in-play');
              },
              onError: () => {
                LoggerService.error('Re-join room failed');
              },
            }
          );
        }

        // Show notification even in foreground
        if (registration.active) {
          const title = payload.notification?.title || 'Bhabi';
          const options: NotificationOptions = {
            body: payload.notification?.body || 'You have a new message',
            icon: '/favicon-192x192.png',
            badge: '/favicon-192x192.png',
            tag: 'bhabi-notification',
            data: payload.data || {},
          };
          registration.active.postMessage({
            type: 'SHOW_NOTIFICATION',
            title,
            options,
          });
        }
      });

      // Listen for messages from service worker (notification clicks)
      if (navigator.serviceWorker) {
        navigator.serviceWorker.addEventListener('message', event => {
          if (event.data?.type === 'NOTIFICATION_CLICKED') {
            LoggerService.info('Notification clicked from service worker');
            const roomID = event.data.roomID;
            if (!isInRoom && roomID) {
              reJoinRoom(
                { roomID },
                {
                  onSuccess: res => {
                    BhabiStorage.setToken(res.token);
                    dispatch(getUser(res.token));
                    nav('/in-play');
                  },
                  onError: () => {
                    LoggerService.error('Re-join room failed');
                  },
                }
              );
            }
          }
        });
      }
    } catch (error) {
      LoggerService.error('Error setting up web push notifications:', error);
    }
  }, [
    updateToken,
    updateLastTimeAsked,
    remindUserAfterSevenDaysIfTheyDenied,
    isInRoom,
    reJoinRoom,
    nav,
  ]);

  const registerNotifications = useCallback(async () => {
    // Only check permissions once to prevent infinite loops
    if (permissionCheckedRef.current) {
      return;
    }
    permissionCheckedRef.current = true;

    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      updateLastTimeAsked();
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive === 'denied') {
      remindUserAfterSevenDaysIfTheyDenied();
      return;
    }

    if (permStatus.receive !== 'granted') {
      LoggerService.error('No notification permissions granted');
      return;
    }

    await PushNotifications.register();
    await addListeners();
  }, [addListeners, remindUserAfterSevenDaysIfTheyDenied, updateLastTimeAsked]);

  useEffect(() => {
    // Register native iOS push notifications
    if (Capacitor.isNativePlatform()) {
      (async function () {
        await registerNotifications();
      })();
    }
    // Register web push notifications for web and PWA
    else {
      (async function () {
        await registerWebPushNotifications();
      })();
    }
  }, [registerNotifications, registerWebPushNotifications]);
};
